import { RTCInboundRtpVideoStreamStats } from './types';
import { getLatestBitrateInKbps } from './StreamSelectorByBitrate';
import { compact } from 'utility/compact';
import { Queue } from 'utility/queue';
import { StreamSelectorBase } from './StreamSelectorBase';

function isBitrateRising(bitrates: number[], threshold: number) {
    const latestBitrate = bitrates[bitrates.length - 1];
    const minBitrate = Math.min(...bitrates);
    const result = latestBitrate > minBitrate * threshold;
    return result;
}

function isLargerThanOthers(targetIndex: number, bitratesTable: Queue<number>[], threshold: number) {
    const lastInTarget = bitratesTable[targetIndex].at(-1);
    if (lastInTarget === undefined) return false;

    const result = bitratesTable.every((xs, index) => {
        if (index === targetIndex) return true; // target itself; just skip

        const last = xs.array.at(-1);
        if (last === undefined) return true; // unavailable stream

        return lastInTarget > last * threshold;
    });
    return result;
}

function findFirst<T>(xs: T[], pred: (x: T, index: number) => boolean): number {
    for (let i = 0; i < xs.length; ++i) {
        if (pred(xs[i], i)) return i;
    }
    return -1;
}

export class StreamSelectorByRisingBitrate extends StreamSelectorBase {
    private bitratesTable: Queue<number>[];

    constructor(numberOfStreams: number) {
        super();

        this.bitratesTable = Array.from({ length: numberOfStreams }, () => new Queue(4));
    }

    label() { return 'rising bitrate'; }

    description() {
        return this.context;
    }

    batchUpdate(streams: Queue<RTCInboundRtpVideoStreamStats>[]): void {
        streams.forEach((stats, index) => {
            const bitrate = getLatestBitrateInKbps(stats.array);
            if (bitrate < 0) return;

            const bitrates = this.bitratesTable[index];
            bitrates.push(bitrate);
        });
    }

    private context: Record<string, unknown> = {};

    get(): number | 'deferred' | 'matched' {
        this.context = {
            stats: this.bitratesTable.map(q => [...q.array])
        };

        const availableIndices = compact(this.bitratesTable.map((xs, index) => xs.length > 0 ? index : undefined));
        this.context = { ...this.context, availableIndices };
        if (availableIndices.length === 0) return 'deferred';
        if (availableIndices.length === 1) return 'matched';

        const firstPromisingStream = findFirst(this.bitratesTable,
            (bitrates, index) => isBitrateRising(bitrates.array, 5)
                && isLargerThanOthers(index, this.bitratesTable, 0.75));
        this.context = { ...this.context, firstPromisingStream };
        if (firstPromisingStream >= 0) {
            return firstPromisingStream;
        }

        return 'matched';
    }
}
